package com.qrkontaq.ui.landing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.qrkontaq.utils.SeoHelper
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/*
 * Platform legal pages (/terms, /privacy, /refund). English is the governing
 * language of these documents — a standard setup for an international SaaS.
 * DRAFTS prepared with AI assistance: have a lawyer review before scaling.
 */

internal data class LegalSection(
    val heading: String,
    val paragraphs: List<String> = emptyList(),
    val bullets: List<String> = emptyList(),
)

internal data class LegalDoc(
    val slug: String,
    val title: String,
    val updated: String,
    val intro: String,
    val sections: List<LegalSection>,
)

private const val OPERATOR =
    "QRkontaq is operated by sole proprietor (ФОП) Dmytro Kushpel, registered in Ukraine " +
        "(\"we\", \"us\"). Contact: support@qrkontaq.com."

internal val TERMS_DOC = LegalDoc(
    slug = "terms",
    title = "Terms of Service",
    updated = "15 August 2026",
    intro = "These Terms govern your use of qrkontaq.com and the QRkontaq service. " +
        "By creating an account you agree to them. $OPERATOR",
    sections = listOf(
        LegalSection(
            "1. The service",
            listOf(
                "QRkontaq lets food-service venues create a menu website on a dedicated subdomain " +
                    "(yourvenue.qrkontaq.com), print QR codes for tables, manage a menu and stop list, " +
                    "receive guest orders and calls, view analytics, run a loyalty program and appear " +
                    "in the QRkontaq guest catalog and map.",
            ),
        ),
        LegalSection(
            "2. Accounts",
            listOf(
                "You must provide accurate information and keep your credentials secure. You are " +
                    "responsible for activity under your account and for the staff members you invite. " +
                    "One account can manage several venues.",
            ),
        ),
        LegalSection(
            "3. Free plan",
            listOf(
                "Every venue starts on the Free plan: the public menu site, orders and tables are " +
                    "available for free with no time limit, and no payment card is required. The Free " +
                    "plan includes one active menu language.",
            ),
        ),
        LegalSection(
            "4. Subscriptions and billing",
            listOf(
                "The Pro plan costs €14.99 per month and adds the loyalty program, guest waiter calls, " +
                    "the guest bill view, additional menu languages and team invitations. When a Pro " +
                    "subscription ends, the venue returns to the Free plan — the menu site keeps " +
                    "working and all data is kept. Prices are in euro (EUR); your card is charged the " +
                    "exact euro amount. Payments are processed by monobank (Universal Bank, Ukraine); " +
                    "we never receive or store your card number.",
                "If you enable auto-renewal, your saved card is charged at the end of each paid period. " +
                    "You can disable auto-renewal at any time in the dashboard — the subscription then " +
                    "simply runs until the end of the paid period. Refunds are described in the Refund " +
                    "Policy.",
            ),
        ),
        LegalSection(
            "5. Your content",
            listOf(
                "Menus, photos, logos and other content you upload remain yours. You grant us a " +
                    "license to host and display them for the purpose of running your menu site and the " +
                    "guest catalog. You are responsible for having the rights to the content you upload " +
                    "and for the accuracy of your menu, prices and allergen information.",
            ),
        ),
        LegalSection(
            "6. Acceptable use",
            bullets = listOf(
                "No unlawful, misleading or infringing content.",
                "Comply with local laws applicable to your venue (consumer protection, alcohol and " +
                    "tobacco advertising, food information regulations).",
                "No attempts to disrupt the service, probe its security or access other tenants' data.",
            ),
        ),
        LegalSection(
            "7. Availability",
            listOf(
                "We aim for the service to be available around the clock but do not guarantee " +
                    "uninterrupted operation. Planned maintenance and factors outside our control may " +
                    "cause downtime. Your data is regularly backed up.",
            ),
        ),
        LegalSection(
            "8. Termination",
            listOf(
                "You can delete a venue or your account at any time in the dashboard; deletion removes " +
                    "the venue's site, menu and uploaded media. We may suspend or terminate accounts that " +
                    "materially violate these Terms, with notice where reasonably possible.",
            ),
        ),
        LegalSection(
            "9. Liability",
            listOf(
                "The service is provided \"as is\". To the extent permitted by law, our aggregate " +
                    "liability for any claims related to the service is limited to the subscription fees " +
                    "you paid in the 12 months preceding the claim. Nothing in these Terms limits " +
                    "liability that cannot be limited by law, including mandatory consumer rights.",
            ),
        ),
        LegalSection(
            "10. Changes and governing law",
            listOf(
                "We may update these Terms; material changes are announced in the dashboard at least 14 " +
                    "days in advance. These Terms are governed by the laws of Ukraine; mandatory consumer " +
                    "protection rules of your country of residence remain unaffected.",
            ),
        ),
    ),
)

internal val PRIVACY_DOC = LegalDoc(
    slug = "privacy",
    title = "Privacy Policy",
    updated = "15 August 2026",
    intro = "This policy explains what personal data QRkontaq processes and why. $OPERATOR " +
        "We act as the data controller for the platform and as a processor for the menu data " +
        "venues publish.",
    sections = listOf(
        LegalSection(
            "1. Data we process",
            bullets = listOf(
                "Venue owners and staff: name, e-mail, password hash, venue details (name, address, " +
                    "logo, menu, photos), subscription and payment history (amounts and status — never " +
                    "card numbers).",
                "Guests visiting a menu site: anonymous usage statistics (page scans by day, table and " +
                    "hour), dish likes stored on the guest's device, ratings and reviews if submitted.",
                "Loyalty program members: e-mail and name from Google or Apple sign-in, loyalty " +
                    "balance and visit history at participating venues.",
                "Technical data: server logs (IP address, user agent) kept for security and debugging.",
            ),
        ),
        LegalSection(
            "2. Purposes and legal bases",
            listOf(
                "We process data to provide the service (contract), to secure and improve it " +
                    "(legitimate interest), and — for optional features like loyalty — based on your " +
                    "action of signing in (consent). We do not sell personal data and do not run " +
                    "third-party advertising or tracking.",
            ),
        ),
        LegalSection(
            "3. Cookies and local storage",
            listOf(
                "We use only functional storage: session tokens to keep you signed in, and device " +
                    "storage for preferences such as language and liked dishes. No advertising cookies.",
            ),
        ),
        LegalSection(
            "4. Service providers",
            bullets = listOf(
                "Hetzner Online GmbH (Germany) — hosting; all data is stored in the EU.",
                "monobank / Universal Bank (Ukraine) — payment processing; card data is handled " +
                    "entirely by the bank.",
                "Google LLC / Apple Inc. — optional sign-in for the loyalty program; Google Fonts " +
                    "served from Google servers.",
            ),
        ),
        LegalSection(
            "5. Retention",
            listOf(
                "Account and venue data is kept while the account is active and deleted on account or " +
                    "venue deletion (backups expire within 30 days). Payment records are kept as long as " +
                    "financial regulations require. Anonymous statistics contain no personal identifiers.",
            ),
        ),
        LegalSection(
            "6. Your rights",
            listOf(
                "Under the GDPR and Ukrainian data protection law you can request access, correction, " +
                    "deletion or export of your data, object to processing, and withdraw consent at any " +
                    "time by e-mailing support@qrkontaq.com. You can also lodge a complaint with your local " +
                    "supervisory authority.",
            ),
        ),
        LegalSection(
            "7. Security",
            listOf(
                "All traffic is encrypted (TLS), passwords are stored hashed, access to production " +
                    "systems is restricted, and each venue's data is isolated per tenant.",
            ),
        ),
        LegalSection(
            "8. Children",
            listOf(
                "The service is intended for business use and is not directed at children under 16.",
            ),
        ),
        LegalSection(
            "9. Changes",
            listOf(
                "We may update this policy; the current version with its effective date is always " +
                    "available at qrkontaq.com/privacy.",
            ),
        ),
    ),
)

internal val REFUND_DOC = LegalDoc(
    slug = "refund",
    title = "Refund Policy",
    updated = "15 August 2026",
    intro = "We want you to pay only for a service that works for you — that's why every venue " +
        "starts on the free plan with no time limit. $OPERATOR",
    sections = listOf(
        LegalSection(
            "1. Try before you pay",
            listOf(
                "The Free plan requires no card and has no time limit, so you can run your menu and " +
                    "evaluate the service for as long as you like before upgrading to Pro.",
            ),
        ),
        LegalSection(
            "2. 14-day money-back guarantee",
            listOf(
                "If you are not satisfied with your first paid subscription, e-mail us within 14 days " +
                    "of the charge and we will refund it in full.",
            ),
        ),
        LegalSection(
            "3. Renewals",
            listOf(
                "Auto-renewal can be switched off at any time in the dashboard — the subscription then " +
                    "runs to the end of the paid period without further charges. An unwanted auto-renewal " +
                    "charge is refunded in full if you contact us within 14 days of the charge.",
            ),
        ),
        LegalSection(
            "4. How refunds are paid",
            listOf(
                "Refunds go to the original payment method via monobank, normally within 5–10 business " +
                    "days. Refunds are issued for the euro amount charged; exchange-rate differences " +
                    "applied by your bank are outside our control.",
            ),
        ),
        LegalSection(
            "5. How to request",
            listOf(
                "E-mail support@qrkontaq.com from the address linked to your account, stating your venue " +
                    "name and payment date. Statutory consumer rights in your country remain unaffected " +
                    "by this policy.",
            ),
        ),
    ),
)

@Composable
internal fun LegalPage(doc: LegalDoc) {
    InjectLandingStyles()
    LaunchedEffect(doc.slug) {
        SeoHelper.updatePageMeta(
            title = "${doc.title} — QRkontaq",
            description = "${doc.title} of the QRkontaq QR menu platform for cafés, restaurants and bars.",
            ogUrl = "https://qrkontaq.com/${doc.slug}",
        )
        SeoHelper.setCanonical("https://qrkontaq.com/${doc.slug}")
    }
    Div({ classes("ql-root", "ql-legal") }) {
        Div({ classes("ql-legal-head") }) {
            Div({ classes("ql-wrap") }) {
                A(href = "/") { Logo() }
            }
        }
        Div({ classes("ql-wrap") }) {
            Div({ classes("ql-legal-body") }) {
                H1 { Text(doc.title) }
                Div({ classes("upd") }) { Text("Effective date: ${doc.updated}") }
                P({ classes("intro") }) { Text(doc.intro) }
                doc.sections.forEach { section ->
                    H2 { Text(section.heading) }
                    section.paragraphs.forEach { paragraph -> P { Text(paragraph) } }
                    if (section.bullets.isNotEmpty()) {
                        Ul { section.bullets.forEach { Li { Text(it) } } }
                    }
                }
                Div({ classes("ql-legal-links") }) {
                    listOf(
                        "terms" to "Terms of Service",
                        "privacy" to "Privacy Policy",
                        "refund" to "Refund Policy",
                    ).forEach { (slug, label) ->
                        if (slug != doc.slug) A(href = "/$slug") { Text(label) }
                    }
                    A(href = "/") { Text("qrkontaq.com") }
                }
                Div({ classes("ql-legal-foot") }) {
                    Span { Text("© 2026 QRkontaq · support@qrkontaq.com") }
                }
            }
        }
    }
}
